/**
 * Liga a função única de modo (§4.1, M27) à janela. Recalcula em `resize`, `orientationchange`, mudança de `topBarsH`
 * (ResizeObserver em `.top-bars`), de `(pointer: coarse)` e de `ui.tv.on`; no máximo uma vez por quadro
 * (`requestAnimationFrame`, AC-4.11-04); escreve o sinal `layout` só quando algum campo muda e espelha
 * `data-mode`/`data-h`/`data-phone`/`data-pointer` e a classe `flow` no `<html>` com `layoutAttributes()`, o mesmo
 * objeto que o esboço de boot aplica. Escritor de `layout` (FE §6.6): este arquivo e main.tsx (via `bootLayout`).
 */
import { effect } from '@preact/signals';
import type { RefObject } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import type { LayoutState } from '../model/types';
import { tvOn } from '../state/derived';
import { layout } from '../state/store';
import {
  layoutAttributes,
  layoutForViewport,
  layoutMode,
  sameLayout,
  type TopBars,
  toLayoutState,
  topBarsHeight,
} from './layoutMode';

export const POINTER_COARSE_QUERY = '(pointer: coarse)';

/** O que o controlador usa da janela (injetável nos testes). */
export interface LayoutWindow {
  innerWidth: number;
  innerHeight: number;
  addEventListener(type: 'resize' | 'orientationchange', fn: () => void): void;
  removeEventListener(type: 'resize' | 'orientationchange', fn: () => void): void;
  requestAnimationFrame(fn: () => void): number;
  cancelAnimationFrame(id: number): void;
  scrollTo(x: number, y: number): void;
  matchMedia?: (query: string) => MediaQueryList;
}

export type LayoutCompute = typeof layoutMode;

export interface LayoutControllerOptions {
  win?: LayoutWindow;
  /** Onde os atributos são escritos (padrão: `document.documentElement`). */
  root?: HTMLElement | null;
  /** A função de modo; injetável para o espião de AC-4.11-04. */
  compute?: LayoutCompute;
  /** `CSS.supports('zoom', '2')` (padrão: o do navegador). */
  zoomSupported?: boolean;
  /** `ui.tv.on` (padrão: o sinal). */
  tv?: () => boolean;
  /** `(pointer: coarse)` (padrão: matchMedia da janela). */
  coarse?: () => boolean;
  /** Avisado quando a altura das faixas do topo muda. */
  onTopBarsH?: (h: number) => void;
}

export interface LayoutController {
  /** Pede um recálculo, coalescido no próximo quadro. */
  schedule(): void;
  /** Recalcula já (montagem e testes); cancela o quadro pendente. */
  flush(): void;
  /** Altura medida de `.top-bars` (px reais); recalcula se mudou. */
  setTopBarsH(h: number): void;
  readonly topBarsH: number;
  dispose(): void;
}

/** `CSS.supports('zoom', '2')`; sem `CSS` (testes, navegador antigo), true: a reserva de §4.2.1 é só para quem não tem. */
export function detectZoomSupport(): boolean {
  return typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || CSS.supports('zoom', '2');
}

function matchCoarse(win: Pick<LayoutWindow, 'matchMedia'> | undefined): boolean {
  try {
    return typeof win?.matchMedia === 'function' && win.matchMedia(POINTER_COARSE_QUERY).matches;
  } catch {
    return false;
  }
}

/** Escreve os atributos de §4.1 "Saídas" no `<html>` (o mesmo objeto que o esboço de boot aplica, M27). */
export function applyLayoutAttributes(root: HTMLElement, s: LayoutState): void {
  const a = layoutAttributes(s);
  root.setAttribute('data-mode', a['data-mode']);
  root.setAttribute('data-h', a['data-h']);
  if (a['data-phone'] === null) root.removeAttribute('data-phone');
  else root.setAttribute('data-phone', '');
  root.setAttribute('data-pointer', a['data-pointer']);
  root.classList.toggle('flow', a.flowClass);
}

/** Escreve o sinal só se algum campo mudou (§4.1); devolve o estado anterior. */
function writeLayout(next: LayoutState): LayoutState {
  const prev = layout.value;
  if (!sameLayout(prev, next)) layout.value = next;
  return prev;
}

/**
 * Valor inicial do `layout`, síncrono, antes do primeiro render (§2.2.3 passo 2, M27): main.tsx chama uma vez com o
 * mesmo `topBarsH` de constantes que o esboço usa (`simulation` = `config.mode === 'simulation'`; `tv` =
 * `route.tvOnEntry && features.tv`). Grava o sinal e os atributos do `<html>`.
 */
export function bootLayout(
  input: TopBars & { tv: boolean },
  opts: {
    win?: Pick<LayoutWindow, 'innerWidth' | 'innerHeight' | 'matchMedia'>;
    root?: HTMLElement | null;
    zoomSupported?: boolean;
  } = {},
): LayoutState {
  const win = opts.win ?? (typeof window === 'undefined' ? undefined : window);
  const coarse = matchCoarse(win);
  const { result } = layoutForViewport(win?.innerWidth ?? 1600, win?.innerHeight ?? 900, {
    ...input,
    coarse: input.coarse ?? coarse,
    zoomSupported: opts.zoomSupported ?? detectZoomSupport(),
  });
  const next = toLayoutState(result, coarse);
  writeLayout(next);
  const root = opts.root ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (root) applyLayoutAttributes(root, next);
  return next;
}

/**
 * Controlador sem Preact (testável): instala os ouvintes, coalesce os pedidos num quadro e escreve sinal e atributos.
 * No app, `h = innerHeight − topBarsH` com a altura MEDIDA de `.top-bars` (§4.1 "Entradas"), que deve bater com as
 * constantes do boot (AC-4.1-05).
 */
export function createLayoutController(opts: LayoutControllerOptions = {}): LayoutController {
  const win = opts.win ?? (window as unknown as LayoutWindow);
  const root = opts.root === undefined ? document.documentElement : opts.root;
  const compute = opts.compute ?? layoutMode;
  const zoomSupported = opts.zoomSupported ?? detectZoomSupport();
  const readTv = opts.tv ?? (() => tvOn.value);
  const readCoarse = opts.coarse ?? (() => matchCoarse(win));
  let topBarsH = 0;
  let frame = 0;
  let first = true;
  let disposed = false;

  const flush = () => {
    if (frame) win.cancelAnimationFrame(frame);
    frame = 0;
    if (disposed) return;
    const result = compute(win.innerWidth, Math.max(0, win.innerHeight - topBarsH), readTv(), zoomSupported);
    const next = toLayoutState(result, readCoarse());
    const prev = writeLayout(next);
    if (root && (first || !sameLayout(prev, next))) applyLayoutAttributes(root, next);
    // §4.1: ao ENTRAR no flow, a página volta ao topo.
    if (prev.mode !== 'flow' && next.mode === 'flow') win.scrollTo(0, 0);
    first = false;
  };
  const schedule = () => {
    if (frame || disposed) return;
    frame = win.requestAnimationFrame(flush);
  };

  win.addEventListener('resize', schedule);
  win.addEventListener('orientationchange', schedule);
  let mq: MediaQueryList | null = null;
  try {
    mq = typeof win.matchMedia === 'function' ? win.matchMedia(POINTER_COARSE_QUERY) : null;
  } catch {
    mq = null;
  }
  mq?.addEventListener?.('change', schedule);
  // ui.tv.on: o computed só muda de valor ao ligar/desligar; a primeira execução do effect é só a inscrição.
  let tvSeen = false;
  const stopTv = opts.tv
    ? () => {}
    : effect(() => {
        void tvOn.value;
        if (tvSeen) schedule();
        tvSeen = true;
      });

  return {
    schedule,
    flush,
    setTopBarsH(h: number) {
      const v = Math.max(0, Math.round(h * 100) / 100);
      if (v === topBarsH) return;
      topBarsH = v;
      opts.onTopBarsH?.(v);
      schedule();
    },
    get topBarsH() {
      return topBarsH;
    },
    dispose() {
      disposed = true;
      if (frame) win.cancelAnimationFrame(frame);
      frame = 0;
      win.removeEventListener('resize', schedule);
      win.removeEventListener('orientationchange', schedule);
      mq?.removeEventListener?.('change', schedule);
      stopTv();
    },
  };
}

/**
 * Altura de `.top-bars` em px reais (fora do zoom, §4.2.2), sem forçar o layout quando as faixas são as conhecidas: a
 * medida na montagem forçava o layout da página inteira dentro da tarefa da 1ª montagem (22% a 28% dela no perfil do
 * boot; DECISOES §49). Sem faixa (produção), 0; só a faixa da simulação, a altura dela (as mesmas constantes do esboço
 * do boot, AC-4.1-05; o PartnerSlot `bar` é P2). Qualquer outra coisa ali é medida. O ResizeObserver confere depois.
 */
export function measureTopBars(el: Element): number {
  if (el.childElementCount === 0) return 0;
  if (el.childElementCount === 1 && el.firstElementChild?.classList.contains('sim-banner')) {
    const tv = el.ownerDocument.documentElement.dataset.mode === 'tv';
    return topBarsHeight({ simulation: true }, tv);
  }
  return el.getBoundingClientRect().height;
}

/**
 * Hook do AppShell: instala o controlador na montagem (recalcula já, sem esperar quadro) e observa `.top-bars`.
 * Devolve `topBarsH` medido, que o useStageZoom usa como `top` do palco.
 */
export function useLayoutMode(topBarsRef: RefObject<HTMLElement>): { topBarsH: number } {
  const [topBarsH, setTopBarsH] = useState(0);
  useLayoutEffect(() => {
    const ctl = createLayoutController({ onTopBarsH: setTopBarsH });
    const el = topBarsRef.current;
    if (el) ctl.setTopBarsH(measureTopBars(el));
    ctl.flush();
    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(() => ctl.setTopBarsH(measureTopBars(el)));
      ro.observe(el);
    }
    return () => {
      ro?.disconnect();
      ctl.dispose();
    };
  }, [topBarsRef]);
  return { topBarsH };
}
